// ПвП.5 (Фаза 5): тонкая обёртка над Colyseus SDK.
//
// Используется тестовым экраном `/colyseus-test` (Фаза 5), позже —
// реальным PvP-боевым экраном. Singleton, потому что один WS-сокет
// достаточно держать на всю сессию (Colyseus сам мультиплексирует
// комнаты).
//
// ВАЖНО про версии: на сервере `colyseus@0.17` + `@colyseus/schema@4.x`.
// Старый клиентский пакет `colyseus.js@0.16` тянет schema 3.x и
// **несовместим по wire-формату**. Поэтому здесь используется
// официальный новый клиент `@colyseus/sdk@0.17` (`colyseus.js`-репо
// архивирован). Если в команде кто-то поставил `colyseus.js` — удалить.
//
// URL-схема:
//   • Прод (VITE_API_URL=https://...): https → wss того же хоста.
//   • Dev (без VITE_API_URL): ws://localhost:3000 — Fastify сервер
//     слушает WS через `@colyseus/ws-transport`, тот же порт что
//     REST. Vite-прокси WebSocket по умолчанию не разворачивает,
//     поэтому ходим напрямую.
//
// Если у вас на VM Nginx не прокидывает Upgrade-заголовки —
// нужно добавить в локацию `/`:
//     proxy_http_version 1.1;
//     proxy_set_header Upgrade $http_upgrade;
//     proxy_set_header Connection "upgrade";

import { Client, Room } from '@colyseus/sdk';
import { getToken } from '../api/client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ENV: any = (import.meta as any).env || {};

function getWsUrl(): string {
  const api = (ENV.VITE_API_URL || '').replace(/\/$/, '');
  if (api) {
    return api.replace(/^http(s?):\/\//, (_m: string, s: string) => `ws${s}://`);
  }
  // Dev fallback — backend на :3000.
  return 'ws://localhost:3000';
}

let _client: Client | null = null;
export function getColyseusClient(): Client {
  if (!_client) {
    _client = new Client(getWsUrl());
  }
  return _client;
}

export interface JoinMatchOptions {
  // ПвП.5b: nick/playerId сервер БЕРЁТ ИЗ JWT — даже если клиент подсунет
  // другие. Оставлены здесь только для совместимости / диагностики.
  playerId?: number;
  nick?: string;
  faction?: 'deus' | 'syntetics' | 'global' | 'livlife';
  heroId?: string;
  // ПвП.10: id колоды игрока в БД. Сервер при startMatch грузит карты
  // из decks-таблицы и передаёт engine.createMatch. Если не указан или
  // колода невалидна — engine fallback'нет на фабричную колоду фракции.
  deckId?: number;
  // Доп. поля от matchmaker'а (читаются filterBy на сервере).
  league?: string;
  mode?: string;
}

// joinOrCreate — Colyseus сам найдёт открытую `match`-комнату с местом
// или создаст новую. Для матчмейкинга по лигам потом сделаем отдельный
// REST-эндпоинт, который вернёт reservationId и комнату.
// ПвП.5b: автоматически прикладываем JWT-токен из localStorage —
// серверный matchmake вернёт 401 без него.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function joinOrCreateMatch(opts: JoinMatchOptions): Promise<Room<any>> {
  const c = getColyseusClient();
  const token = getToken();
  return c.joinOrCreate('match', { ...opts, token });
}

// ПвП.4b: «текущая» PvP-комната — singleton чтобы MatchmakingScreen
// мог передать её ColyseusTestScreen'у через router navigate (location
// state не сериализует Room-объект). Очищается на leave/error.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _currentRoom: Room<any> | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setCurrentRoom(room: Room<any> | null) {
  _currentRoom = room;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCurrentRoom(): Room<any> | null {
  return _currentRoom;
}
